import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ToastContext = createContext(null);

function uid() {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function toastStyles(type) {
  // base “néon”
  const base = {
    wrap:
      "border bg-slate-950/80 text-slate-100 " +
      "backdrop-blur-md " +
      "shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_12px_40px_rgba(0,0,0,0.55)]",
    // petit highlight interne
    inner:
      "bg-gradient-to-b from-white/10 to-white/0",
    // bouton close
    close:
      "text-slate-300 hover:text-white hover:bg-white/10",
  };

  switch (type) {
    case "success":
      return {
        ...base,
        ring: "border-emerald-400/40 shadow-[0_0_18px_rgba(16,185,129,0.35),0_12px_40px_rgba(0,0,0,0.55)]",
        dot: "bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.8)]",
        title: "text-emerald-200",
      };
    case "error":
      return {
        ...base,
        ring: "border-rose-400/40 shadow-[0_0_18px_rgba(244,63,94,0.35),0_12px_40px_rgba(0,0,0,0.55)]",
        dot: "bg-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.8)]",
        title: "text-rose-200",
      };
    case "info":
    default:
      return {
        ...base,
        ring: "border-indigo-400/40 shadow-[0_0_18px_rgba(99,102,241,0.35),0_12px_40px_rgba(0,0,0,0.55)]",
        dot: "bg-indigo-400 shadow-[0_0_12px_rgba(99,102,241,0.8)]",
        title: "text-indigo-200",
      };
  }
}

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, opts = {}) => {
      const {
        type = "info",
        title = "",
        duration = 4000,
      } = opts;

      const id = uid();
      const toast = { id, type, title, message };

      setItems((prev) => [...prev, toast]);

      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }

      return id;
    },
    [dismiss]
  );

  const api = useMemo(() => {
    return {
      show: (message, opts) => push(message, opts),
      success: (message, opts) => push(message, { ...(opts || {}), type: "success" }),
      error: (message, opts) => push(message, { ...(opts || {}), type: "error" }),
      info: (message, opts) => push(message, { ...(opts || {}), type: "info" }),
      dismiss,
      clear: () => setItems([]),
    };
  }, [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Toast stack */}
      <div className="fixed bottom-4 right-4 z-[9999] flex w-[92vw] max-w-sm flex-col gap-2">

        {items.map((t) => {
          const s = toastStyles(t.type);

          return (
            <div
              key={t.id}
              className={[
                "rounded-2xl border overflow-hidden",
                s.wrap,
                s.ring,
              ].join(" ")}
            >
              {/* petit sheen néon */}
              <div className={["pointer-events-none h-8 w-full", s.inner].join(" ")} />

              <div className="flex items-start gap-3 px-3 pb-3 -mt-6">
                {/* dot néon */}
                <span
                  className={[
                    "mt-2 h-2.5 w-2.5 shrink-0 rounded-full",
                    s.dot,
                  ].join(" ")}
                />

                <div className="min-w-0 flex-1">
                  {t.title ? (
                    <div className={["text-[11px] font-extrabold uppercase tracking-[0.18em]", s.title].join(" ")}>
                      {t.title}
                    </div>
                  ) : null}

                  <div className="mt-1 text-xs leading-relaxed text-slate-100/90">
                    {t.message}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  className={[
                    "rounded-xl px-2 py-1 text-xs transition",
                    s.close,
                  ].join(" ")}
                  aria-label="Fermer"
                  title="Fermer"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}

      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider />");
  }
  return ctx;
}
